import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { HomeIcon, PencilIcon, TrashIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';

interface Property {
  id: string;
  title: string;
  location: string;
  status: 'active' | 'pending' | 'inactive';
  imageUrl: string;
  price: number;
}

const mockProperties: Property[] = [
  {
    id: '1',
    title: 'Luxury Villa with Pool',
    location: 'Miami, FL',
    status: 'active',
    imageUrl: 'https://picsum.photos/400/300?random=1',
    price: 250
  },
  {
    id: '2',
    title: 'Downtown Apartment',
    location: 'New York, NY',
    status: 'pending',
    imageUrl: 'https://picsum.photos/400/300?random=2',
    price: 150
  },
];

const HostHomesPage: React.FC = () => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);

  const handleDelete = (propertyId: string) => {
    setSelectedPropertyId(propertyId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    // Handle delete logic here
    setIsDeleteModalOpen(false);
    setSelectedPropertyId(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Your Properties</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage and update your property listings
            </p>
          </div>
          <button className="inline-flex items-center px-4 py-2 bg-airbnb-red text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-colors">
            <PlusCircleIcon className="h-5 w-5 mr-2" />
            Add New Property
          </button>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {mockProperties.map((property) => (
            <motion.div
              key={property.id}
              className="bg-white rounded-xl shadow-sm overflow-hidden"
              whileHover={{ y: -4 }}
              transition={{ duration: 0.2 }}
            >
              <div className="relative aspect-video">
                <img
                  src={property.imageUrl}
                  alt={property.title}
                  className="object-cover w-full h-full"
                />
                <div className="absolute top-2 right-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    property.status === 'active' ? 'bg-green-100 text-green-800' :
                    property.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {property.status.charAt(0).toUpperCase() + property.status.slice(1)}
                  </span>
                </div>
              </div>
              
              <div className="p-4">
                <h3 className="text-lg font-medium text-gray-900">{property.title}</h3>
                <p className="mt-1 text-sm text-gray-500">{property.location}</p>
                <p className="mt-2 text-lg font-semibold text-gray-900">${property.price} per night</p>
                
                <div className="mt-4 flex gap-2">
                  <button className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(property.id)}
                    className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-red-300 rounded-lg text-sm font-medium text-red-700 hover:bg-red-50 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <Dialog
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          className="relative z-50"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="mx-auto max-w-sm rounded-2xl bg-white p-6">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Delete Property
              </Dialog.Title>
              <Dialog.Description className="mt-2 text-sm text-gray-500">
                Are you sure you want to delete this property? This action cannot be undone.
              </Dialog.Description>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default HostHomesPage;